/*!
 * lev-klaviyo.js  --  WQ-130, narrowed by D1 (DEC-489, 2026-10-17)
 *
 * D1 (founder order 2026-10-17, Veronica V3 ROT Tier 1 and U5, growth/retention/v3/d1/):
 * the website tools send nothing to Klaviyo any more. Three locks, each enough on its own:
 *   a. The tool pages do not load this file at all, nor window.LEV_KLAVIYO
 *      (layout/theme.liquid asks snippets/lev-werkzeug-seite.liquid).
 *   b. The tool keys (spritztag, ziel, cluster, phase, tool_segmente) and the tool metrics
 *      (Eiweiss-Rechner, Erhaltungs-Rechner, Beschwerden-Kompass, Spritztag-Post,
 *      Spritztag-Erinnerung, Countdown angefordert) are gone from this module.
 *   c. A closed list of surfaces (SURFACES): a Lead from any other surface returns before
 *      an address or a property is touched, so no tool name and no tool page ever lands in
 *      tool, quelle_seite or custom_source.
 * What is left is the document request of the GLP-1 report (lev-report-glp1.liquid):
 * address, the consent log of the newsletter list, the document marker, the source fields.
 *
 * PRECONDITION, to be satisfied BEFORE window.LEV_KLAVIYO is ever filled in:
 * the target list in Klaviyo must have DOUBLE OPT-IN switched ON. The subscribe call
 * below asks Klaviyo to subscribe an address; whether that produces a confirmation
 * mail or an immediate subscription is a property of the LIST, never of this file.
 * With double opt-in off, the same call adds the address directly, which is unlawful
 * in Germany (Paragraf 7 UWG) and breaks the promise the form copy makes. Turn it on
 * first, then set companyId and listId. See growth/klaviyo/wiring-WQ-130.md.
 *
 * Applied 2026-09-12 (AP-100): shipped as assets/lev-klaviyo.js, loaded deferred from
 * layout/theme.liquid of theme 201664758094; the list TDFY6w stands on double opt-in
 * (DEC-126). Consent-log fields (tool, herkunft, quelle_seite, einwilligung_fassung,
 * custom_source) follow the newsletter page and the blog capture (WQ-446).
 *
 * The binding rules it encodes (growth/specs/integration-hooks.md, WQ-106, Toby run
 * 2026-07-31, narrowed by D1):
 *   1. Only the Lead event of a listed surface goes anywhere. Interaction events and every
 *      other surface return immediately, before any address or property is touched.
 *   2. No health parameter is accepted at all (D1). The module carries no scalar
 *      allowlist any more; a key a caller passes beyond tool, email and segments is ignored.
 *   3. report_glp1 names the requested document. It never becomes an audience attribute.
 *      Segments other than the document markers are dropped.
 *   4. This module has exactly two sinks, both of them Klaviyo; any future sink outside the
 *      ESP takes its payload from sanitize() and nothing else.
 *
 * Guarded by: python tools/verify_consent_layer.py --klaviyo  (rules K1 to K9). K3 and K4
 * encode the pre-D1 tool design (tool_segmente, spritztag in the allowlist) and fail on
 * this file by design until the guard is updated (a founder step, gate logic).
 */
(function () {
  'use strict';

  // Wire format. Endpoint, path and revision are DEFAULTS, overridable from the theme,
  // because they are Klaviyo's to change, not ours. 2025-04-15 is the newest revision
  // this repo has in use (oscarAgent/setup_klaviyo.py, tools/klaviyo/setup_*.py). Check
  // all three against the live documentation before the first real submit; a wrong wire
  // format fails silently by design.
  var API_BASE = 'https://a.klaviyo.com';
  var SUBSCRIBE_PATH = '/client/subscriptions/';
  var REVISION = '2025-04-15';

  // D1: the only surfaces that may still subscribe through this module. A tool page is
  // never one of them; adding one needs the lawyer first (Veronica V3, U5).
  var SURFACES = ['glp1-report'];

  // Internal Klaviyo metric names, never customer copy.
  var METRICS = {
    'glp1-report': 'GLP-1-Report angefordert'
  };
  var DEFAULT_METRIC = 'Lead erfasst';

  // Document markers. Not health data, not audience attributes (Rule 3).
  var DOCUMENT_SEGMENTS = ['report_glp1'];

  function config() {
    var raw = window.LEV_KLAVIYO;
    if (!raw || typeof raw !== 'object') { return null; }
    return {
      companyId: raw.companyId || '',
      listId: raw.listId || '',
      apiBase: raw.apiBase || API_BASE,
      subscribePath: raw.subscribePath || SUBSCRIBE_PATH,
      revision: raw.revision || REVISION
    };
  }

  function metricFor(tool) {
    return METRICS[tool] || DEFAULT_METRIC;
  }

  // params.email is the documented path. The DOM fallback exists only for the window in
  // which the theme carries the script tag but not yet the email: additions. It never
  // guesses: zero or more than one field means no address, and no address means no sink.
  function resolveEmail(params) {
    var given = (params && typeof params.email === 'string') ? params.email.trim() : '';
    if (given) { return given; }
    var fields = document.querySelectorAll('[data-lead-form] input[type=email]');
    if (fields.length !== 1) { return ''; }
    return (fields[0].value || '').trim();
  }

  // ONE builder, output for BOTH sinks. Splitting this would let the two payloads drift
  // apart, and the rules with them.
  function buildProps(params) {
    var p = params || {};
    var segments = Object.prototype.toString.call(p.segments) === '[object Array]' ? p.segments : [];
    var documents = [];
    for (var i = 0; i < segments.length; i++) {
      if (DOCUMENT_SEGMENTS.indexOf(segments[i]) !== -1) { documents.push(segments[i]); }
    }
    var props = {};
    if (documents.length) { props.dokument_angefordert = documents[0]; }
    // Consent log (WQ-446): which surface, which consent wording, from which page. Same
    // field names as lev-newsletter.liquid and lev-blog-capture.liquid. Not health data,
    // not audience attributes. herkunft is the founder-fixed origin value and may be
    // overwritten by any newsletter form (DEC-126); the report's dokument_angefordert
    // above stays the only document marker.
    var tool = (typeof p.tool === 'string') ? p.tool : '';
    if (tool) { props.tool = tool; }
    props.herkunft = 'newsletter';
    props.quelle_seite = String((window.location && window.location.pathname) || '').replace(/^\//, '');
    props.einwilligung_fassung = 'WQ-106 ' + tool + ', Stand 2026-07-31';
    // WQ-547: Anmeldequelle ergaenzen (kanal, quelle_methode und die zugehoerigen
    // Felder aus growth/klaviyo/source-schema.json). Eigene, geschlossene Allowlist in
    // lev-source.js, disjunkt von den Feldern oben. Fehlt das Modul, bleibt die Nutzlast
    // unveraendert: eine Anmeldung scheitert nie an der Quellenmessung.
    if (window.LEV_SOURCE && typeof window.LEV_SOURCE.merge === 'function') {
      try { window.LEV_SOURCE.merge(props); } catch (e) { /* nie das Formular brechen */ }
    }
    return props;
  }

  // Rule 4. The single permitted payload for any sink that is not the ESP. Exported so a
  // later Conversions API step has one obvious thing to call, deliberately NOT attached to
  // a network call here: the CAPI stays deferred (tools/pixel/README.md, point 6).
  function sanitize(params) {
    return {tool: (params && params.tool) || ''};
  }

  // Sink A: profile plus metric, onsite JS. No effect without window.klaviyo.
  function sendProfile(conf, email, props, tool) {
    if (!window.klaviyo || typeof window.klaviyo.push !== 'function') { return; }
    var identify = {'$email': email};
    for (var key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key)) { identify[key] = props[key]; }
    }
    try {
      window.klaviyo.push(['identify', identify]);
      window.klaviyo.push(['track', metricFor(tool), props]);
    } catch (e) { /* noop */ }
  }

  // Sink B: the list subscription. This, not the profile, is what triggers the
  // confirmation mail. Takes the built props, never the raw params: the rules above must
  // apply to both sinks.
  function subscribe(conf, email, props) {
    if (!conf.companyId || !conf.listId) { return; }
    var query = new URLSearchParams();
    query.set('company_id', conf.companyId);
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'levanura.de/' + (props.quelle_seite || '') + ' ' + (props.tool || ''),
          profile: {
            data: {
              type: 'profile',
              attributes: {email: email, properties: props}
            }
          }
        },
        relationships: {list: {data: {type: 'list', id: conf.listId}}}
      }
    };
    try {
      window.fetch(conf.apiBase + conf.subscribePath + '?' + query.toString(), {
        method: 'POST',
        keepalive: true,
        headers: {'Content-Type': 'application/vnd.api+json', revision: conf.revision},
        body: JSON.stringify(payload)
      })['catch'](function () { /* a failed subscribe never breaks the form */ });
    } catch (e) { /* noop */ }
  }

  function LEV_TRACK(ev, params) {
    if (ev !== 'Lead') { return; }
    var tool = (params && typeof params.tool === 'string') ? params.tool : '';
    if (SURFACES.indexOf(tool) === -1) { return; }
    var conf = config();
    if (!conf) { return; }
    var email = resolveEmail(params);
    if (!email) { return; }
    var props = buildProps(params);
    if (!props) { return; }
    sendProfile(conf, email, props, tool);
    subscribe(conf, email, props);
  }

  LEV_TRACK.sanitize = sanitize;
  window.LEV_TRACK = LEV_TRACK;
})();
